'use client'

import { useQueryClient } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { createContext, type ReactNode, useContext, useEffect } from 'react'

import type {
  ModelsActionType,
  ModelsUserFullInfoResponse
} from '@/api/generated-api'
import type { AuthModalView } from '@/app/[locale]/components/Auth/AuthModal/types'
import type { AuthEntityType } from '@/app/[locale]/components/Auth/AuthModal/types'
import { URLS } from '@/configs/urls'
import { useNotificationsSession } from '@/features/notifications/hooks/useNotificationsSession'
import { detachBeforeLogout } from '@/features/notifications/lib/webPush'
import { USER_ROLE_CLUB } from '@/features/tasks/fanTasksAccess'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import { useUserMe } from '@/hooks/useUserMe'
import { usePathname, useRouter } from '@/i18n/routing'
import { pushLogoutToDataLayer } from '@/lib/analytics/userIdentity'
import { isProtectedAppPath } from '@/lib/auth/protectedRoutes'
import { shouldRedirectHomeOnClubLogout } from '@/lib/auth/shouldRedirectHomeOnClubLogout'
import type { EntryPoint } from '@/lib/productAnalytics'
import { AuthProviderDialogs } from '@/providers/AuthProviderDialogs'
import { showToast } from '@/providers/ToastProvider/ToastMessage'
import { loadApiClient } from '@/shared/api/lazyClient'
import { useAuthStore } from '@/stores/authStore'
import { useModalStore } from '@/stores/modalStore'
import { getSessionTokenPayload } from '@/utils/auth'
import { clearTokens } from '@/utils/refreshTokens'

const ACTION_TYPE_RECOVERY_PASSWORD = 'RECOVERY_PASSWORD' as ModelsActionType

// ── Backward-compatible context interface ──
interface AuthContextType {
  openAuthModal: (initialView?: AuthModalView, entryPoint?: EntryPoint) => void
  openConfirmationModal: (
    onConfirm: (code: string) => void,
    onResend: () => void | Promise<string | undefined>,
    options?: {
      email?: string
      actionType?: ModelsActionType
      verificationId?: string
      /** Sign-up only: the role, so «Back» returns to the form with it. */
      entityType?: AuthEntityType
    }
  ) => void
  openModal: (
    modalType: 'auth' | 'confirmation' | 'forgot' | 'username' | 'recover',
    onUsernameSubmit?: (username: string) => void,
    onRecoverPasswordSubmit?: (password: string) => void
  ) => void
  closeAllModals: () => void
  isAuthenticated: boolean
  user: ModelsUserFullInfoResponse | null
  login: () => void
  logout: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const authStore = useAuthStore()
  const modalStore = useModalStore()
  const { data: userData, refetch } = useUserMe()
  const router = useRouter()
  const pathname = usePathname()
  const queryClient = useQueryClient()
  const isAuthenticated = useAuthStore(s => s.isAuthenticated)
  const tConfirm = useTranslations('auth.confirmationCode')
  const translateError = useApiErrorTranslator()

  // Notification cache, Web Push subscription and push clicks follow the
  // session wherever it changes, not only through the methods below.
  useNotificationsSession()

  // ── Check auth status on mount and after client navigations ──
  useEffect(() => {
    authStore.checkAuth()
  }, [])

  useEffect(() => {
    useAuthStore.getState().checkAuth()
  }, [pathname])

  // Session ended while user is still on a private route (logout, expiry, 401).
  useEffect(() => {
    if (isAuthenticated) {
      return
    }

    if (isProtectedAppPath(pathname)) {
      router.replace(URLS.MAIN)
    }
  }, [isAuthenticated, pathname, router])

  // ── Sync user data from TanStack Query → authStore ──
  useEffect(() => {
    if (userData) {
      authStore.setUser(userData)
      if (modalStore.activeModal !== 'auth') {
        modalStore.closeAll()
      }
    } else if (!authStore.isAuthenticated) {
      authStore.setUser(null)
    }
  }, [userData, authStore.isAuthenticated, modalStore.activeModal])

  // ── Context methods (delegate to stores) ──
  const login = () => {
    authStore.login()
    router.refresh()
    refetch()
    queryClient.invalidateQueries({ queryKey: ['clubs', 'me'] })
    if (modalStore.activeModal !== 'auth') {
      modalStore.closeAll()
    }
  }

  const logout = async () => {
    // Capture role before tokens are cleared — after logout JWT is gone.
    const isClubUser = Boolean(
      (getSessionTokenPayload()?.roles ?? authStore.user?.roles)?.includes(
        USER_ROLE_CLUB
      )
    )
    const shouldLeaveProtectedRoute = isProtectedAppPath(pathname)
    const shouldLeaveClubCabinet = shouldRedirectHomeOnClubLogout(
      pathname,
      isClubUser
    )

    // A browser's push subscription outlives the session: without this the
    // next account on this browser would get this one's pushes. The server
    // has to be told while the tokens still work.
    await detachBeforeLogout()

    pushLogoutToDataLayer('user')
    authStore.logout()
    await clearTokens()
    queryClient.removeQueries({ queryKey: ['me'] })
    queryClient.removeQueries({ queryKey: ['user'] })
    queryClient.removeQueries({ queryKey: ['tasks'] })
    queryClient.removeQueries({ queryKey: ['clubs', 'me'] })
    modalStore.setReferralCode(null)
    modalStore.setReferralEntityType(null)
    modalStore.setSignupDefaultEntityType(null)
    modalStore.setAuthModalInitialView('login')
    modalStore.setAuthModalSuccessType('signup')
    sessionStorage.removeItem('arena_referral_code')

    if (shouldLeaveProtectedRoute || shouldLeaveClubCabinet) {
      router.replace(URLS.MAIN)
      return
    }

    router.refresh()
  }

  const openConfirmationModal = (
    onConfirm: (code: string) => void,
    onResend: () => void | Promise<string | undefined>,
    options?: {
      email?: string
      actionType?: ModelsActionType
      verificationId?: string
      /** Sign-up only: the role, so «Back» returns to the form with it. */
      entityType?: AuthEntityType
    }
  ) => {
    const wrappedOnConfirm = async (code: string) => {
      await onConfirm(code)
    }
    const wrappedOnResend = async () => {
      const newVerificationId = await onResend()
      if (newVerificationId) {
        // Zustand getState() always returns current — no stale closure
        const current = useModalStore.getState().confirmationData
        useModalStore.setState({
          confirmationData: { ...current, verificationId: newVerificationId }
        })
      }
    }
    modalStore.openConfirmationModal(
      {
        email: options?.email,
        actionType: options?.actionType,
        verificationId: options?.verificationId,
        entityType: options?.entityType
      },
      { onConfirm: wrappedOnConfirm, onResend: wrappedOnResend }
    )
  }

  const openModal = (
    modalType: 'auth' | 'confirmation' | 'forgot' | 'username' | 'recover',
    onUsernameSubmit?: (username: string) => void,
    onRecoverPasswordSubmit?: (password: string) => void
  ) => {
    if (modalType === 'username' && onUsernameSubmit) {
      modalStore.setUsernameSubmitHandler(onUsernameSubmit)
    }
    if (modalType === 'recover' && onRecoverPasswordSubmit) {
      modalStore.setRecoverPasswordSubmitHandler(onRecoverPasswordSubmit)
    }

    const typeMap: Record<string, typeof modalStore.activeModal> = {
      auth: 'auth',
      confirmation: 'confirmation',
      forgot: 'forgotPassword',
      username: 'username',
      recover: 'recoverPassword'
    }
    modalStore.openModal(typeMap[modalType] ?? null)
  }

  const handleForgotPasswordSubmit = async (email: string) => {
    try {
      const api = await loadApiClient()
      const userExistsResponse = await api.v2AuthCheckUserExistsByEmailList({
        email
      })
      if (!userExistsResponse.is_exists) {
        // OCE-681: bubble a stable machine-readable code; the form translates it.
        throw new Error('ACCOUNT_NOT_EXIST')
      }

      const verificationResponse = await api.v2AuthEmailVerificationCreate({
        email,
        action_type: ACTION_TYPE_RECOVERY_PASSWORD
      })
      const verificationId = verificationResponse.verification_id
      if (!verificationId) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('No verification ID received')
        }
        modalStore.closeAll()
        showToast({ type: 'error', title: 'Failed to initiate verification' })
        modalStore.openModal('auth')
        return
      }

      openConfirmationModal(
        async (code: string) => {
          // OCE-814: pre-flight verify lives here (not in ConfirmationModal)
          // because the recovery flow opens a *second* modal for the new
          // password — without an explicit gate the user would type a new
          // password and only then learn the OTP was wrong. signup/signin
          // skip this step: their submit endpoints verify the code as the
          // first thing they do.
          const currentData = useModalStore.getState().confirmationData
          const currentVerificationId =
            currentData?.verificationId || verificationId
          try {
            const verifyResponse = await api.v2AuthVerifyCodeCreate({
              action_type: ACTION_TYPE_RECOVERY_PASSWORD,
              email,
              verification_code: Number(code),
              verification_id: currentVerificationId
            })
            if (!verifyResponse.is_valid) {
              modalStore.setConfirmationError(tConfirm('invalidCode'))
              return
            }
          } catch (verifyError: unknown) {
            const r = translateError(verifyError, { otpContext: true })
            const message =
              r.kind === 'view' ? tConfirm('invalidCode') : r.message
            modalStore.setConfirmationError(message)
            return
          }

          openModal('recover', undefined, async (password: string) => {
            try {
              const latestData = useModalStore.getState().confirmationData
              const latestVerificationId =
                latestData?.verificationId || verificationId
              await api.v2AuthRecoveryPasswordCreate({
                email,
                new_password: password,
                verification_code: Number(code),
                verification_id: latestVerificationId
              })

              modalStore.openModal('passwordResetSuccess')
            } catch (error: any) {
              if (error.response?.data?.message) {
                showToast({
                  type: 'error',
                  title: error.response.data.message
                })
                return
              }
              if (process.env.NODE_ENV !== 'production') {
                console.error('Password recovery error:', error)
              }
            }
          })
        },
        async () => {
          try {
            const response = await api.v2AuthCheckUserExistsByEmailList({
              email
            })
            if (!response.is_exists) {
              showToast({
                type: 'error',
                title: 'User with this email does not exist'
              })
              return
            }
            const res = await api.v2AuthEmailVerificationCreate({
              email,
              action_type: ACTION_TYPE_RECOVERY_PASSWORD
            })
            if (res.verification_id) {
              const current = useModalStore.getState().confirmationData
              useModalStore.setState({
                confirmationData: {
                  ...current,
                  verificationId: res.verification_id
                }
              })
              return res.verification_id
            }
            return undefined
          } catch (resendError) {
            if (process.env.NODE_ENV !== 'production') {
              console.error('Resend error:', resendError)
            }
            showToast({
              type: 'error',
              title: 'Failed to resend verification code'
            })
            return undefined
          }
        },
        {
          email,
          actionType: ACTION_TYPE_RECOVERY_PASSWORD,
          verificationId
        }
      )
    } catch (error: any) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Forgot password error:', error)
      }
      throw error
    }
  }

  // ── Context value ──
  const contextValue: AuthContextType = {
    openAuthModal: (
      initialView: AuthModalView = 'login',
      entryPoint?: EntryPoint
    ) => {
      modalStore.setAuthModalInitialView(initialView)
      modalStore.setAuthModalEntryPoint(entryPoint ?? null)
      modalStore.openModal('auth')
    },
    openConfirmationModal,
    openModal,
    closeAllModals: () => modalStore.closeAll(),
    isAuthenticated: authStore.isAuthenticated,
    user: authStore.user,
    login,
    logout
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      <AuthProviderDialogs
        onLogin={login}
        onForgotPasswordSubmit={handleForgotPasswordSubmit}
        openModal={openModal}
      />
    </AuthContext.Provider>
  )
}
